// =======================
// src/paginas/secretaria/TrocarCredenciais.jsx
// =======================

import React, { useContext, useEffect, useMemo, useState } from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";

import {
  EmailOutlined,
  InfoOutlined,
  LockOutlined,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";

import TransicaoPagina from "../../componentes/comuns/TransicaoPagina";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import { api } from "@api/api";
import { CondicaoContext } from "../../contexto";

import { botaoCancelar, botaoSalvar } from "../../estilos/botoes";
import { ajustarTamanhoFonteSre } from "../../estilos/global";

// =========================================================
// VALIDAÇÃO DA SENHA
// =========================================================

const senhaValida = (valor = "") => {
  const temMinimo8 = valor.length >= 8;
  const temMaiuscula = /[A-Z]/.test(valor);
  const temMinuscula = /[a-z]/.test(valor);
  const temNumero = /\d/.test(valor);
  const temEspecial = /[@$!%*?&]/.test(valor);

  const somenteCaracteresPermitidos = /^[A-Za-z\d@$!%*?&]+$/.test(valor);

  return (
    temMinimo8 &&
    temMaiuscula &&
    temMinuscula &&
    temNumero &&
    temEspecial &&
    somenteCaracteresPermitidos
  );
};

export default function TrocarCredenciais() {
  // =========================================================
  // DADOS
  // =========================================================

  const [emailAcesso, setEmailAcesso] = useState("");

  const [carregandoEmail, setCarregandoEmail] = useState(true);

  const [senha, setSenha] = useState("");

  const [confirmarSenha, setConfirmarSenha] = useState("");

  // =========================================================
  // VISUALIZAÇÃO DAS SENHAS
  // =========================================================

  const [showPassword, setShowPassword] = useState(false);

  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // =========================================================
  // CARREGAMENTO / FEEDBACK
  // =========================================================

  const [loading, setLoading] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [snackbarMessage, setSnackbarMessage] = useState("");

  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // =========================================================
  // CONTEXTO / NAVEGAÇÃO
  // =========================================================

  const { setFirstAccess } = useContext(CondicaoContext);

  const navigate = useNavigate();

  // =========================================================
  // VALIDAÇÃO
  // =========================================================

  const senhaAtendeRequisitos = useMemo(() => senhaValida(senha), [senha]);

  // =========================================================
  // CARREGAMENTO DO E-MAIL DE ACESSO
  // =========================================================

  useEffect(() => {
    let ativo = true;

    const carregarUsuario = async () => {
      try {
        const response = await axios.get(api.auth(), {
          withCredentials: true,
        });

        if (!ativo) {
          return;
        }

        setEmailAcesso(response.data?.email || "");
      } catch (error) {
        console.error("Erro ao carregar dados do usuário:", error);

        if (!ativo) {
          return;
        }

        setSnackbarMessage("Não foi possível carregar o e-mail de acesso.");

        setSnackbarSeverity("error");

        setSnackbarOpen(true);
      } finally {
        if (ativo) {
          setCarregandoEmail(false);
        }
      }
    };

    carregarUsuario();

    return () => {
      ativo = false;
    };
  }, []);

  // =========================================================
  // VISUALIZAÇÃO DA SENHA
  // =========================================================

  const handleClickShowPassword = () => {
    setShowPassword((atual) => !atual);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword((atual) => !atual);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  // =========================================================
  // CANCELAR
  // =========================================================

  const handleCancelar = () => {
    navigate("/admin");
  };

  // =========================================================
  // SALVAR
  // =========================================================

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (loading) {
      return;
    }

    if (!senha.trim()) {
      setSnackbarMessage("Informe a nova senha.");

      setSnackbarSeverity("error");

      setSnackbarOpen(true);

      return;
    }

    if (!senhaAtendeRequisitos) {
      setSnackbarMessage(
        "A senha não atende aos requisitos mínimos informados.",
      );

      setSnackbarSeverity("error");

      setSnackbarOpen(true);

      return;
    }

    if (senha !== confirmarSenha) {
      setSnackbarMessage("A confirmação da senha não confere.");

      setSnackbarSeverity("error");

      setSnackbarOpen(true);

      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        api.updateSreCredentials(),

        {
          senha,
        },

        {
          withCredentials: true,
        },
      );

      if (response.status === 200) {
        setFirstAccess(false);

        setSnackbarMessage("Acesso atualizado com sucesso!");

        setSnackbarSeverity("success");

        setSnackbarOpen(true);

        setTimeout(() => {
          navigate("/admin");
        }, 1000);
      }
    } catch (error) {
      console.error("Erro ao atualizar a senha:", error);

      const mensagem =
        error.response?.data?.message ?? error.response?.data?.msg;

      setSnackbarMessage(
        (Array.isArray(mensagem) ? mensagem.join(" ") : mensagem) ||
          "Não foi possível atualizar o acesso.",
      );

      setSnackbarSeverity("error");

      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // FEEDBACK
  // =========================================================

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <TransicaoPagina variante="navegacao">
      <Box
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          padding: "8px 0 12px",
        }}
      >
        <Container
          component="main"
          maxWidth={false}
          sx={{
            width: "100%",

            maxWidth: 520,

            backgroundColor: "#ffffff",

            px: {
              xs: 2.5,
              sm: 3.25,
            },

            py: {
              xs: 2.25,
              sm: 2.6,
            },

            borderRadius: "14px",

            borderTop: "4px solid #1e88e5",

            boxShadow: "0 8px 26px rgba(15, 23, 42, 0.09)",
          }}
        >
          {/* ===================================================
            CABEÇALHO
        =================================================== */}

          <Box
            sx={{
              display: "flex",

              flexDirection: "column",

              alignItems: "center",

              mb: 1.8,
            }}
          >
            <Box
              sx={{
                width: 58,

                height: 58,

                borderRadius: "50%",

                backgroundColor: "#eaf4ff",

                display: "flex",

                alignItems: "center",

                justifyContent: "center",

                mb: 1.2,
              }}
            >
              <LockOutlined
                sx={{
                  fontSize: 30,

                  color: "#1976d2",
                }}
              />
            </Box>

            <Typography
              component="h1"
              sx={{
                color: "#13386b",

                fontSize: ajustarTamanhoFonteSre("2.125rem"),

                fontWeight: 700,

                lineHeight: 1.2,

                textAlign: "center",

                mb: 0.6,
              }}
            >
              Primeiro acesso
            </Typography>

            <Typography
              sx={{
                color: "#53657d",

                fontSize: ajustarTamanhoFonteSre("1rem"),

                lineHeight: 1.45,

                textAlign: "center",

                maxWidth: 430,
              }}
            >
              Atualize sua senha provisória para continuar no sistema. O e-mail
              de acesso foi definido pelo Administrador e não poderá ser
              alterado neste momento.
            </Typography>
          </Box>

          {/* ===================================================
            FORMULÁRIO
        =================================================== */}

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              width: "100%",
            }}
          >
            {/* =================================================
              E-MAIL
          ================================================= */}

            <TextField
              label="E-mail de acesso"
              size="small"
              fullWidth
              value={carregandoEmail ? "Carregando..." : emailAcesso}
              InputProps={{
                readOnly: true,

                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined
                      sx={{
                        color: "#6c7a8c",

                        fontSize: 20,
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 1.35,

                "& .MuiOutlinedInput-root": {
                  minHeight: 43,

                  backgroundColor: "#eef2f6",

                  borderRadius: 1.15,

                  "& fieldset": {
                    borderColor: "#d7dee7",
                  },

                  "&:hover fieldset": {
                    borderColor: "#d7dee7",
                  },

                  "&.Mui-focused fieldset": {
                    borderColor: "#c7d2df",
                  },
                },

                "& .MuiInputBase-input": {
                  fontSize: ajustarTamanhoFonteSre("1rem"),

                  color: "#40546d",

                  WebkitTextFillColor: "#40546d",

                  cursor: "default",
                },

                "& .MuiInputLabel-root": {
                  fontSize: ajustarTamanhoFonteSre("1rem"),
                },
              }}
            />

            {/* =================================================
              NOVA SENHA
          ================================================= */}

            <TextField
              label="Nova senha"
              type={showPassword ? "text" : "password"}
              size="small"
              fullWidth
              required
              value={senha}
              onChange={(event) => setSenha(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined
                      sx={{
                        color: "#6c7a8c",

                        fontSize: 20,
                      }}
                    />
                  </InputAdornment>
                ),

                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={
                        showPassword ? "Ocultar senha" : "Mostrar senha"
                      }
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? (
                        <VisibilityOff
                          sx={{
                            fontSize: 21,
                          }}
                        />
                      ) : (
                        <Visibility
                          sx={{
                            fontSize: 21,
                          }}
                        />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 1.35,

                "& .MuiOutlinedInput-root": {
                  minHeight: 43,

                  backgroundColor: "#ffffff",

                  borderRadius: 1.15,
                },

                "& .MuiInputBase-input": {
                  fontSize: ajustarTamanhoFonteSre("1rem"),
                },

                "& .MuiInputLabel-root": {
                  fontSize: ajustarTamanhoFonteSre("1rem"),
                },
              }}
            />

            {/* =================================================
              CONFIRMAR SENHA
          ================================================= */}

            <TextField
              label="Confirmar nova senha"
              type={showConfirmPassword ? "text" : "password"}
              size="small"
              fullWidth
              required
              value={confirmarSenha}
              onChange={(event) => setConfirmarSenha(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined
                      sx={{
                        color: "#6c7a8c",

                        fontSize: 20,
                      }}
                    />
                  </InputAdornment>
                ),

                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={
                        showConfirmPassword ? "Ocultar senha" : "Mostrar senha"
                      }
                      onClick={handleClickShowConfirmPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      size="small"
                    >
                      {showConfirmPassword ? (
                        <VisibilityOff
                          sx={{
                            fontSize: 21,
                          }}
                        />
                      ) : (
                        <Visibility
                          sx={{
                            fontSize: 21,
                          }}
                        />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 1.6,

                "& .MuiOutlinedInput-root": {
                  minHeight: 43,

                  backgroundColor: "#ffffff",

                  borderRadius: 1.15,
                },

                "& .MuiInputBase-input": {
                  fontSize: ajustarTamanhoFonteSre("1rem"),
                },

                "& .MuiInputLabel-root": {
                  fontSize: ajustarTamanhoFonteSre("1rem"),
                },
              }}
            />

            {/* =================================================
              REQUISITOS DA SENHA
          ================================================= */}

            <Box
              sx={{
                display: "flex",

                gap: 1,

                alignItems: "flex-start",

                backgroundColor: "#eef5fd",

                border: "1px solid #d9e8f7",

                borderRadius: "9px",

                p: 1.4,

                mb: 1.8,
              }}
            >
              <InfoOutlined
                sx={{
                  color: "#1976d2",

                  fontSize: 20,

                  mt: 0.05,

                  flexShrink: 0,
                }}
              />

              <Box>
                <Typography
                  sx={{
                    fontWeight: 700,

                    color: "#1357b8",

                    mb: 0.4,

                    fontSize: ajustarTamanhoFonteSre("1rem"),
                  }}
                >
                  Requisitos da senha
                </Typography>

                <Box
                  component="ul"
                  sx={{
                    m: 0,

                    pl: 2,

                    color: "#53657d",

                    fontSize: ajustarTamanhoFonteSre("0.875rem"),

                    lineHeight: 1.55,
                  }}
                >
                  <li>Mínimo de 8 caracteres</li>

                  <li>Pelo menos 1 letra maiúscula (A–Z)</li>

                  <li>Pelo menos 1 letra minúscula (a–z)</li>

                  <li>Pelo menos 1 número (0–9)</li>

                  <li>Pelo menos 1 caractere especial (@ $ ! % * ? &amp;)</li>
                </Box>
              </Box>
            </Box>

            {/* =================================================
              AÇÕES
          ================================================= */}

            <Box
              sx={{
                display: "flex",

                justifyContent: "flex-end",

                gap: 1,

                pt: 1.4,

                borderTop: "1px solid #e3ebf3",

                flexWrap: "wrap",
              }}
            >
              <Button
                type="button"
                variant="outlined"
                onClick={handleCancelar}
                disabled={loading}
                sx={{
                  ...botaoCancelar,

                  minWidth: 120,

                  minHeight: 40,

                  fontSize: ajustarTamanhoFonteSre("0.875rem"),
                }}
              >
                Cancelar
              </Button>

              <Button
                type="submit"
                variant="outlined"
                disabled={loading || carregandoEmail}
                sx={{
                  ...botaoSalvar,

                  minWidth: 155,

                  minHeight: 40,

                  fontSize: ajustarTamanhoFonteSre("0.875rem"),
                }}
              >
                {loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Atualizar acesso"
                )}
              </Button>
            </Box>
          </Box>

          {/* ===================================================
            FEEDBACK
        =================================================== */}

          <Snackbar
            open={snackbarOpen}
            autoHideDuration={4000}
            onClose={handleSnackbarClose}
            anchorOrigin={{
              vertical: "top",

              horizontal: "center",
            }}
          >
            <Alert
              onClose={handleSnackbarClose}
              severity={snackbarSeverity}
              sx={{
                width: "100%",
              }}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </Container>
      </Box>
    </TransicaoPagina>
  );
}
